/**
 * Air refresh-resume wiring — see booking-flow/BookingFlowResumer.tsx and the plan.
 * Kept in its own file (not flowConfig.ts) so the search re-run can import `searchAir`
 * without a circular import: flowConfig.ts imports `airPersistConfig` from here; this
 * file imports nothing back from flowConfig.ts except the `AirFlowState` type.
 */
import type { ShoppingCart } from '../../api/types/shoppingCart';
import type { AirSearchResponse, OriginDestinationOption } from '../../api/types/air';
import { searchAir } from '../../api/endpoints/air';
import { bookingDraftKey } from '../../booking-flow/draftStorage';
import {
  cartIsPaid,
  clampStepIndex,
  compact,
  type BookingFlowPersistConfig,
  type ResumeAdapter,
} from '../../booking-flow/resume';
import type { AirFlowState } from './flowConfig';

/** Aramadan sonra ödemeyi tamamlamak için tanınan süre (ms). `AirFlowState.searchDeadline`
 *  mutlak epoch tutar, böylece sayfa yenilense de geri sayım devam eder. */
export const SEARCH_DEADLINE_MS = 15 * 60 * 1000;

/** Sağlayıcının fiyat kilidi (`OriginDestinationOption.expireAt`) 15 dakikadan erken
 *  bitiyorsa geri sayım ona uysun — aksi hâlde UI "14:59 kaldı" derken Allocate
 *  sağlayıcı tarafında "teklif süresi doldu" ile patlar. `expireAt` TZ'siz ISO string
 *  geldiği için (`new Date` → yerel saat) parse şüpheli; bariz geçmiş/geçersiz değerde
 *  eski 15 dk davranışına düşülür ki taze bir arama yanlışlıkla "süreniz doldu"
 *  modalına takılmasın. */
export function computeSearchDeadline(res: AirSearchResponse | undefined): number {
  const fallback = Date.now() + SEARCH_DEADLINE_MS;
  const options: OriginDestinationOption[] = [
    ...(res?.originDestinationOptions ?? []),
    ...(res?.multiOriginDestinationOptions ?? []).flat(),
  ];
  const expiries = options
    .map((o) => o.expireAt)
    .filter((v): v is string => !!v)
    .map((v) => new Date(v).getTime())
    .filter((t) => Number.isFinite(t));
  if (!expiries.length) return fallback;
  const cushioned = Math.min(...expiries) - 30_000; // 30 sn pay
  if (cushioned <= Date.now()) return fallback;
  return Math.min(cushioned, fallback);
}

/** Must match `airFlowConfig.steps` order (asserted in flowConfig.test.ts). Bump
 *  `airPersistConfig.version` on any change here. */
const AIR_STEP_ORDER = [
  'search',
  'allocate',
  'passengers',
  'seatSelection',
  'payment',
  'finalize',
] as const;
const idx = (id: string) => AIR_STEP_ORDER.indexOf(id as (typeof AIR_STEP_ORDER)[number]);

const FLOOR = idx('passengers');
const PAYMENT = idx('payment');

const airPartialize = (s: AirFlowState): Partial<AirFlowState> =>
  compact({
    searchRequest: s.searchRequest,
    selectedOption: s.selectedOption,
    selectedFare: s.selectedFare,
    selectedReturnOption: s.selectedReturnOption,
    selectedReturnFare: s.selectedReturnFare,
    multiCitySelections: s.multiCitySelections,
    passengers: s.passengers,
    contact: s.contact,
    selectedCurrentAccount: s.selectedCurrentAccount,
    billingInformation: s.billingInformation,
    paymentType: s.paymentType,
    paymentResult: s.paymentResult,
    searchDeadline: s.searchDeadline,
    wantsInsurance: s.wantsInsurance,
    // keep only the id — the live cart is re-fetched on resume
    shoppingCart: s.shoppingCart?.id ? { id: s.shoppingCart.id } : undefined,
  });

export const airPersistConfig: BookingFlowPersistConfig<AirFlowState> = {
  key: bookingDraftKey('air'),
  version: 2,
  floorStepId: 'passengers',
  searchResultStepId: 'allocate',
  getDeadline: (s) => s.searchDeadline,
  partialize: airPartialize,
  /** Biletlenmiş (finalize edilmiş) ya da onaya gönderilmiş bir sepet için taslak YAZMA.
   *  Aksi hâlde tamamlanmış taslak resume edilir, `AirFlowInner`'ın `flowComplete` etkisi
   *  tetiklenir ve kullanıcı `/air`'e her girdiğinde rezervasyon sayfasına fırlatılır
   *  (bounce). Ödenmiş ama biletlenmemiş sepet hâlâ saklanır — kullanıcı dönüp
   *  "Kesinleştir"i deneyebilsin. */
  shouldPersist: (s, stepIndex) => {
    if (s.shoppingCart?.isFinalized || s.sentForApproval) return false;
    if (stepIndex > 0) return true;
    return Object.keys(airPartialize(s)).length > 0;
  },
};

export const airResumeAdapter: ResumeAdapter<AirFlowState> = {
  getCartId: (draft) => draft.shoppingCart?.id ?? undefined,
  applyResumedCart: (cart) => ({ shoppingCart: cart }),
  resolveResumeStepId: (cart: ShoppingCart, savedStepId: string) => {
    const paid = cartIsPaid(cart);
    if (paid) return 'finalize'; // finalized ⇒ document view via loosened guard; not finalized ⇒ step after payment
    if (cart.isPriceChanged) return 'passengers';
    const saved = idx(savedStepId);
    return AIR_STEP_ORDER[clampStepIndex(saved >= 0 ? saved : FLOOR, FLOOR, PAYMENT)];
  },
  rerunSearch: async (draft) => {
    if (!draft.searchRequest) return null;
    // Çok noktalı dahil tek `Air/Search` — bkz. flowConfig.ts `runSearch`.
    const searchResult = await searchAir(draft.searchRequest);
    return { searchResult, searchDeadline: computeSearchDeadline(searchResult) };
  },
};
